
import { computed, defineComponent, getCurrentInstance, ref } from 'vue'
import { useProject } from '../../../../../hooks/useProject'
import { useBillingStore } from '../../../../../store/stores/billing'
import { applyCancelSubscriptionQueryParams } from '@/helpers/channels/getConnectorChannelDisplayName'
import { getAvailableSubscriptionPeriods } from '../../../subscriptionPeriodDiscount'
import { formatOrderPromocodesTitle, formatPromocodeName } from '@/helpers/billing/promocodeDiscountLabel'

export default defineComponent({
    props: {},
    setup(props, { emit }) {
        const { project } = useProject()
        const billingStore = useBillingStore()
        const root = getCurrentInstance()?.proxy
        const unsubscribeLoading = ref()
        const currentTariffData = computed(() => billingStore.getCurrentTariff(project.value.id))
        const selectedPromoData = computed(() =>
            billingStore.selectedTariffId
                ? billingStore.getTariffPromocode(billingStore.selectedTariffId)
                : null
        )
        const selectedTariff = computed(() => billingStore.getTariffById(billingStore.selectedTariffId!)!)
        const isSelectedPeriodAvailable = computed(() => {
            const period = billingStore.selectedSubscriptionPeriod
            if (!period || !selectedTariff.value) return false
            return getAvailableSubscriptionPeriods(selectedTariff.value).includes(period)
        })
        const selectedTariffPriceDescription = computed(() => {
            if (billingStore.selectedSubscriptionPeriod === '1') return 'В месяц'
            if (billingStore.selectedSubscriptionPeriod === '3') return 'Раз в 3 месяца'
            if (billingStore.selectedSubscriptionPeriod === '6') return 'В 6 месяцев'
            if (billingStore.selectedSubscriptionPeriod === '12') return 'В 12 месяцев'
            if (billingStore.selectedSubscriptionPeriod === '24') return 'В 24 месяца'
        })
        const currentTariffPriceDescription = computed(() => {
            if (!currentTariffData.value) return ''
            if (currentTariffData.value.periodInMonths === 1) return 'В месяц'
            if (currentTariffData.value.periodInMonths === 3) return 'Раз в 3 месяца'
            if (currentTariffData.value.periodInMonths === 6) return 'В 6 месяцев'
            if (currentTariffData.value.periodInMonths === 12) return 'В 12 месяцев'
            if (currentTariffData.value.periodInMonths === 24) return 'В 24 месяца'
        })
        const currentTariffName = computed(() =>
            currentTariffData.value ? currentTariffData.value.name : 'Нет тарифа'
        )
        const currentPromoName = computed(() => formatOrderPromocodesTitle(currentTariffData.value?.promocodeData))
        const selectedPromoName = computed(() =>
            selectedPromoData.value
                ? 'Промокод ' + formatPromocodeName(selectedPromoData.value.promocode, selectedPromoData.value)
                : ''
        )
        const currentTariffState = computed(() => billingStore.getCurrentTariffState(project.value.id))
        const currentTariffFormattedPrice = computed(() => {
            if (!currentTariffData.value || !currentTariffData.value?.price.toPayPerPeriodWithDiscount)
                return '0 ₽'
            return billingStore.formatPrice(currentTariffData.value?.price.toPayPerPeriodWithDiscount)
        })
        const currentTariffFormattedPromoPrice = computed(() => {
            return billingStore.formatPrice(currentTariffData.value?.price.regularPay)
        })

        const selectedTariffPrice = computed(() =>
            billingStore.getTarrifRegularPrice(
                selectedTariff.value!.id,
                billingStore.selectedSubscriptionPeriod!
            )
        )
        const selectedTariffPromoPrice = computed(() =>
            billingStore.getTarrifPromoPrice(
                selectedTariff.value!.id,
                billingStore.selectedSubscriptionPeriod!
            )
        )
        const selectedTariffFormattedPrice = computed(() =>
            billingStore.formatPrice(selectedTariffPrice.value)
        )
        const selectedTariffFormattedPromoPrice = computed(() =>
            billingStore.formatPrice(selectedTariffPromoPrice.value)
        )
        // Зачёркнутая цена — для процента. Сумма даётся один раз на заказ, а здесь цена периода: при
        // помесячной оплате первое списание за 2 месяца, и «цена минус сумма» не совпала бы со счётом.
        const showSelectedPromoPrice = computed(
            () =>
                Boolean(selectedPromoData.value) &&
                selectedPromoData.value?.discountType !== 'amount' &&
                selectedTariffPromoPrice.value < selectedTariffPrice.value
        )

        const openPaymentModal = () => {
            billingStore.setAddChannelContext(null)
            billingStore.setPaymentModalOpened(true)
        }
        const openProlongModal = () => {
            billingStore.setAddChannelContext(null)
            billingStore.setPaymentModalOpened(true, true)
        }

        const selectedTariffIsSame = computed(() => {
            return Boolean(
                currentTariffData.value?.integrationId &&
                    billingStore.selectedTariffId === currentTariffData.value?.integrationId
            )
        })

        const unsubscribe = () => {
            const subscriptionId = currentTariffData.value?.subscriptionId
            if (!subscriptionId) {
                root?.$store?.dispatch('callNotify', 'Не удалось определить подписку для отмены')
                return
            }

            const periodTo =
                currentTariffData.value?.endOfUse ||
                currentTariffData.value?.activeUntil ||
                currentTariffData.value?.nextPayment
            root?.$store?.commit('setChannelCreationMode', false)
            root?.$store?.commit('setChannelCreationStage', null)
            applyCancelSubscriptionQueryParams({ renewalMode: null, channelName: null, periodTo: periodTo || null })

            root?.$store?.commit(
                'setConnectorSettingsSidebarId',
                `cancel-subscription:tariff:${subscriptionId}`
            )
            root?.$store?.commit('setIsConnectorSettingsSidebar', true)
        }
        return {
            currentTariffData,
            selectedTariff,
            isSelectedPeriodAvailable,
            currentTariffState,
            currentTariffFormattedPrice,
            selectedTariffFormattedPrice,
            selectedTariffFormattedPromoPrice,
            currentTariffFormattedPromoPrice,
            selectedTariffPriceDescription,
            currentTariffPriceDescription,
            currentTariffName,
            selectedPromoName,
            showSelectedPromoPrice,
            openPaymentModal,
            openProlongModal,
            currentPromoName,
            selectedTariffIsSame,
            unsubscribe,
            unsubscribeLoading,
        }
    },
})
